import React, { useState } from "react";
import styled from "styled-components";
import { Copy, Check } from "react-feather";
import { Button } from "@cinesys/ui/components";
/**
 * Copy-to-clipboard control for the Gamebook tab (game id, per-play UID). Built on the shared
 * `Button` (icon-only when there's no `label`, icon+label when there is — e.g. the game-id chip in
 * `GamebookScreen.tsx`'s `MatchupMeta`, where the id text itself IS the button rather than a
 * separate copy icon bolted on after it) rather than a bare `<button>`, so it picks up the same
 * hover/tooltip/focus behavior as every other icon button in this app (Addison, round 20: "bring in
 * the copy button that uses the Button component behind the scenes" — the specific other
 * implementation named wasn't one I could find/access, so this rebuilds the same idea directly on
 * `@cinesys/ui`'s own `Button`).
 *
 * Uses `Button`'s `type="default"` (the same blue-accent color scheme as the "Generate gamebook"
 * button elsewhere in this file) and matches `DownloadButton`'s own icon/label sizing exactly
 * (`iconSize={15}`, `font-size: 12.5px` on the label) — Addison, round 22: "you're still not
 * using type default... needs to be typed default and use a smaller font smaller icon similar to
 * the... CSV font and icon size everywhere." `Button`'s `StyledButton` hardcodes `font-size: 15px`
 * on the label `<span>` with no prop to shrink it, so `Wrap` overrides it here instead.
 *
 * `hoverOnly` renders at `opacity: 0` by default — the PARENT cell (`UidCell` in
 * `GamebookScreen.tsx`) reveals it via `&:hover .copy-btn { opacity: 1 }`, matched on this
 * component's own stable `copy-btn` class, so hovering anywhere in that cell (not just the tiny
 * icon itself) reveals the button, mirroring the reference mock's `.prow:hover .copy{opacity:1}`.
 */
const Wrap = styled.span<{ $hoverOnly?: boolean; $hasLabel?: boolean }>`
  display: inline-flex;
  opacity: ${(p) => (p.$hoverOnly ? 0 : 1)};
  transition: opacity 0.12s;

  button span {
    font-size: 12.5px;
  }

  /* Only when there's a label (the game-id button) — Button's own icon/label padding (3px each
     side of the label, 2px on the icon's outer edge only) leaves the icon reading as crowded right
     up against the label text. Icon-only buttons (no label — e.g. the per-row UID copy icon) skip
     this: they have no sibling to space away from, and a stray margin would just push the icon
     off-center inside its round button. */
  ${(p) =>
    p.$hasLabel &&
    `button i {
      margin-left: 4px;
    }`}
`;

export interface CopyButtonProps {
  /** The exact text copied to the clipboard. */
  value: string;
  /** Tooltip while idle — swaps to "Copied!" for a beat after a successful copy. */
  title?: string;
  /** When set, renders as an icon+label button (the label is usually `value` itself) instead of a
   *  bare icon — e.g. the game id, where the id text IS the clickable/copyable control. */
  label?: string;
  /** Starts hidden (`opacity: 0`); an ancestor reveals it on hover via the `copy-btn` class. */
  hoverOnly?: boolean;
  className?: string;
  /** Overrides `Button`'s own footprint/icon sizing. Default (unset) keeps this component's
   *  usual `iconSize={15}` — Addison: the per-row icon-only UID copy button, at `Button`'s own
   *  default 32px circular footprint, read as way too big sitting inline next to 12-13px row
   *  text, so that call site passes smaller explicit values. */
  size?: number;
  iconSize?: number;
}

const RESET_MS = 1400;

const CopyButton = ({
  value,
  title,
  label,
  hoverOnly,
  className,
  size,
  iconSize,
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const onClick = (e: React.MouseEvent) => {
    // Every call site so far nests this inside something else clickable (a table row that
    // expands on click) — never let the copy action also trigger that.
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard?.writeText(value).catch(() => {
      // No fallback: a clipboard write can only fail from a permissions/context issue (e.g.
      // non-HTTPS), which a retry can't fix — nothing useful to do beyond not crashing.
    });
    setCopied(true);
    window.setTimeout(() => setCopied(false), RESET_MS);
  };

  return (
    <Wrap
      className={className ? `copy-btn ${className}` : "copy-btn"}
      $hoverOnly={hoverOnly}
      $hasLabel={!!label}
    >
      <Button
        icon={copied ? Check : Copy}
        iconRight={!!label}
        label={label}
        onClick={onClick}
        tooltip={copied ? "Copied!" : title ?? "Copy"}
        type="default"
        size={size}
        iconSize={iconSize ?? 15}
        borderless
      />
    </Wrap>
  );
};

export default CopyButton;
